
import React, { useState } from 'react';
import { Challenge } from '../../types';
import { HAS_CLOUD_SUPPORT } from '../../services/api';

interface ChallengeArenaProps {
    challenges: Challenge[];
    onlineMembers: any[];
    onJoin: (challenge: Challenge) => void;
    lang?: 'en' | 'fr';
}

const MemberCard: React.FC<{ member: any }> = ({ member }) => {
    const [invited, setInvited] = useState(false);

    const handleInvite = () => {
        setInvited(true);
        setTimeout(() => setInvited(false), 3000);
    };

    return (
        <div className="min-w-[160px] bg-white/5 p-6 rounded-[32px] border border-white/10 flex flex-col items-center gap-4 group hover:border-smart-gold/30 transition-all cursor-pointer">
            <div className="relative">
                <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-2xl grayscale group-hover:grayscale-0 transition-all">🎸</div>
                <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-4 border-black ${member.status === 'in_studio' ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
            </div>
            <div className="text-center">
                <div className="text-sm font-black text-white">{member.name}</div>
                <div className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mt-1">Level {member.level}</div>
            </div>
            <button 
                onClick={handleInvite}
                disabled={invited}
                className={`text-[8px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-all ${invited ? 'bg-emerald-500 text-black' : 'bg-white/5 hover:bg-smart-gold hover:text-black'}`}
            >
                {invited ? 'Invité ✓' : 'Défier'}
            </button>
        </div>
    );
};

export const ChallengeArena: React.FC<ChallengeArenaProps> = ({ challenges, onlineMembers, onJoin, lang = 'fr' }) => {
    const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
    const isFr = lang === 'fr';

    // GATE: CLOUD REQUIRED
    if (!HAS_CLOUD_SUPPORT) {
        return (
            <div className="flex flex-col items-center justify-center h-[400px] text-center space-y-6 opacity-60">
                <div className="w-24 h-24 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-4xl">
                    ☁️
                </div>
                <div className="max-w-md">
                    <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">
                        {isFr ? "Arena Hors-Ligne" : "Arena Offline"}
                    </h3>
                    <p className="text-slate-400 text-sm mt-2">
                        {isFr 
                            ? "Arena nécessite une synchronisation cloud pour garantir l'équité des compétitions. Veuillez configurer un backend pour activer ce mode." 
                            : "Arena requires cloud synchronization to ensure competition fairness. Please configure a backend to enable this mode."}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-10 animate-fade-in pb-20">
            {/* LIVE ACTIVITY FEED */}
            <div className="bg-black/40 border border-white/5 rounded-3xl p-4 overflow-hidden relative group">
                <div className="flex items-center gap-6 animate-marquee whitespace-nowrap">
                    {[1,2,3].map(i => (
                        <div key={i} className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            <span className="text-white">Jimi_Vibe</span> vient de battre le record sur <span className="text-smart-accent">Hey Joe</span> (94%)
                        </div>
                    ))}
                </div>
            </div>

            {/* ONLINE NOW SECTION */}
            <section className="space-y-6">
                <div className="flex justify-between items-end px-2">
                    <h3 className="text-[12px] font-black text-slate-400 uppercase tracking-[0.4em]">
                        {isFr ? "Membres en Studio Live" : "Members in Live Studio"}
                    </h3>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                        <span className="text-[10px] font-mono text-emerald-500">{onlineMembers.length} {isFr ? "Connectés" : "Online"}</span>
                    </div>
                </div>
                <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
                    {onlineMembers.map(m => (
                        <MemberCard key={m.id} member={m} />
                    ))}
                </div>
            </section>

            {/* ACTIVE CHALLENGES */}
            <section className="space-y-8">
                 <div className="flex justify-between items-end px-2">
                    <h3 className="text-[12px] font-black text-slate-400 uppercase tracking-[0.4em]">
                        {isFr ? "Arena : Championnats du Club" : "Arena: Club Championships"}
                    </h3>
                </div>
                <div className="grid grid-cols-1 gap-6">
                    {challenges.map(c => (
                        <div 
                            key={c.id} 
                            onClick={() => setSelectedChallenge(c)}
                            className={`bg-gradient-to-r from-[#002A34] to-black p-8 rounded-[40px] border transition-all cursor-pointer relative overflow-hidden group ${selectedChallenge?.id === c.id ? 'border-smart-gold ring-1 ring-smart-gold/50 scale-[1.02]' : 'border-white/10 hover:border-white/20'}`}
                        >
                            <div className="absolute -right-10 -bottom-10 opacity-10 text-[120px] italic font-black pointer-events-none select-none text-white group-hover:scale-110 transition-transform">
                                VS
                            </div>
                            
                            <div className="flex flex-col md:flex-row justify-between gap-8 relative z-10">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <div className="px-3 py-1 bg-smart-accent rounded-full text-[9px] font-black text-white uppercase tracking-widest">{isFr ? "Temps restant :" : "Time left:"} {c.deadline}</div>
                                        <div className="text-slate-500 text-[10px] font-mono uppercase">{c.participants} Participants</div>
                                    </div>
                                    <div>
                                        <h4 className="text-4xl font-black text-white italic uppercase tracking-tighter leading-none">{c.songTitle}</h4>
                                        <p className="text-slate-400 text-sm mt-2">{isFr ? "Défis lancé par" : "Challenge by"} <span className="text-white font-bold">{c.creator}</span> • {c.bpm} BPM</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-10">
                                    <div className="text-right">
                                        <div className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mb-1">Score Master</div>
                                        <div className="text-5xl font-black text-smart-gold italic tracking-tighter">{c.scoreToBeat}<span className="text-lg">/100</span></div>
                                    </div>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onJoin(c); }}
                                        className="h-20 w-20 rounded-full bg-white text-black flex items-center justify-center shadow-[0_15px_40px_rgba(255,255,255,0.2)] hover:scale-110 active:scale-95 transition-all group/play"
                                    >
                                        <span className="text-3xl group-hover/play:scale-125 transition-transform">⚔️</span>
                                    </button>
                                </div>
                            </div>

                            {/* Classement Rétractable */}
                            {selectedChallenge?.id === c.id && (
                                <div className="mt-10 pt-8 border-t border-white/5 animate-fade-in-up">
                                    <div className="flex justify-between items-center mb-6">
                                        <h5 className="text-[10px] font-black uppercase text-smart-gold tracking-widest">Hall of Fame - Top 3</h5>
                                        <button className="text-[8px] font-bold uppercase text-slate-500 underline">{isFr ? "Voir tout le classement" : "See leaderboard"}</button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3">
                                        {c.leaderboard?.slice(0, 3).map((entry, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5">
                                                <div className="flex items-center gap-4">
                                                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${idx === 0 ? 'bg-smart-gold text-black' : 'bg-white/10 text-white'}`}>{idx + 1}</span>
                                                    <span className="font-bold text-white uppercase text-xs">{entry.userName}</span>
                                                </div>
                                                <span className="font-mono text-smart-accent font-black">{entry.score}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <style>{`
                @keyframes marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .animate-marquee {
                    animation: marquee 30s linear infinite;
                }
            `}</style>
        </div>
    );
};
